import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { invoices as invoicesApi } from '../api';
import { formatCurrency, formatDate, isOverdue } from '../utils/format';
import PageHeader from '../components/PageHeader';
import StatusBadge from '../components/StatusBadge';
import Modal from '../components/Modal';
import { PERIOD_OPTIONS } from '../utils/periods';

const STATUS_OPTIONS = ['all', 'draft', 'sent', 'paid', 'overdue', 'cancelled'];

export default function Invoices() {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [periodFilter, setPeriodFilter] = useState('all_time');
  const [search, setSearch] = useState('');
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [hasAnyInvoices, setHasAnyInvoices] = useState(false);

  const load = () => {
    const params = {};
    if (statusFilter !== 'all') params.status = statusFilter;
    if (periodFilter !== 'all_time') params.period = periodFilter;
    invoicesApi.list(params).then(r => {
      setData(r.data);
      setLoading(false);
    }).catch(() => setLoading(false));
  };

  useEffect(() => { load(); }, [statusFilter, periodFilter]);

  useEffect(() => {
    invoicesApi.list({}).then(r => {
      setHasAnyInvoices(r.data.length > 0);
    }).catch(() => {});
  }, []);

  const confirmDelete = async () => {
    await invoicesApi.delete(deleteTarget.id);
    setDeleteTarget(null);
    load();
  };

  const markSent = async (inv) => {
    await invoicesApi.updateStatus(inv.id, 'sent');
    load();
  };

  const filtered = data.filter(inv =>
    (inv.invoice_number || '').toLowerCase().includes(search.toLowerCase()) ||
    (inv.client_name || '').toLowerCase().includes(search.toLowerCase())
  );

  const getOutstandingClass = (inv) => {
    const outstanding = inv.total_amount - (inv.amount_paid || 0);
    if (outstanding <= 0) return 'text-green-600';
    if (isOverdue(inv)) return 'text-red-600 font-semibold';
    if (inv.amount_paid > 0) return 'text-amber-600 font-semibold';
    return 'text-gray-900';
  };

  return (
    <div>
      <PageHeader
        title="Invoices"
        subtitle={`${data.length} invoice${data.length !== 1 ? 's' : ''}`}
        actions={
          <Link to="/invoices/new" className="btn-primary flex items-center gap-2">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            New Invoice
          </Link>
        }
      />

      {/* Filters */}
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex min-w-0 max-w-full overflow-x-auto rounded-lg border border-gray-200 bg-white">
            {STATUS_OPTIONS.map(s => (
              <button
                key={s}
                onClick={() => setStatusFilter(s)}
                className={`px-3 py-1.5 text-sm font-medium capitalize transition-colors whitespace-nowrap flex-shrink-0 ${
                  statusFilter === s
                    ? 'bg-primary-700 text-white'
                    : 'text-gray-600 hover:bg-gray-50'
                }`}
              >
                {s}
              </button>
            ))}
          </div>
          <input
            className="input max-w-xs"
            placeholder="Search invoices..."
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
        </div>
        <select
          className="input w-auto"
          value={periodFilter}
          onChange={e => setPeriodFilter(e.target.value)}
        >
          {PERIOD_OPTIONS.map(o => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-40">
          <div className="w-7 h-7 border-4 border-primary-700 border-t-transparent rounded-full animate-spin" />
        </div>
      ) : (
        <>
          {filtered.length === 0 && (
            <div className="text-center py-16">
              {!hasAnyInvoices && statusFilter === 'all' && periodFilter === 'all_time' ? (
                <>
                  <p className="text-lg font-semibold text-gray-700">No invoices yet</p>
                  <p className="text-sm text-gray-500 mt-1">Create your first invoice to get started</p>
                  <Link to="/invoices/new" className="btn-primary mt-4 inline-block">Create Invoice</Link>
                </>
              ) : (
                <p className="text-gray-500 text-sm">
                  {statusFilter === 'all'
                    ? 'No invoices for the selected time period'
                    : `No ${statusFilter.charAt(0).toUpperCase() + statusFilter.slice(1)} invoices for the selected time period`}
                </p>
              )}
            </div>
          )}

          {filtered.length > 0 && (
            <>
              {/* Desktop table */}
              <div className="hidden md:block card overflow-hidden p-0">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b border-gray-100">
                <tr>
                  <th className="text-left px-4 py-3 text-gray-500 font-medium">Invoice #</th>
                  <th className="text-left px-4 py-3 text-gray-500 font-medium">Client</th>
                  <th className="text-left px-4 py-3 text-gray-500 font-medium">Date</th>
                  <th className="text-left px-4 py-3 text-gray-500 font-medium">Due Date</th>
                  <th className="text-right px-4 py-3 text-gray-500 font-medium">Total</th>
                  <th className="text-right px-4 py-3 text-gray-500 font-medium">Paid</th>
                  <th className="text-right px-4 py-3 text-gray-500 font-medium">Outstanding</th>
                  <th className="text-center px-4 py-3 text-gray-500 font-medium">Status</th>
                  <th className="px-4 py-3" />
                </tr>
              </thead>
              <tbody>
                {filtered.map(inv => {
                  const overdue = isOverdue(inv);
                  const outstanding = inv.total_amount - (inv.amount_paid || 0);
                  return (
                    <tr key={inv.id} className={`border-b border-gray-50 hover:bg-gray-50 ${overdue ? 'bg-red-50/30' : ''}`}>
                      <td className="px-4 py-3">
                        <Link to={`/invoices/${inv.id}`} className="text-primary-600 hover:underline font-medium">
                          {inv.invoice_number}
                        </Link>
                      </td>
                      <td className="px-4 py-3 text-gray-700">
                        <div>{inv.client_name}</div>
                        {inv.client_company && <div className="text-xs text-gray-400">{inv.client_company}</div>}
                      </td>
                      <td className="px-4 py-3 text-gray-500">{formatDate(inv.invoice_date)}</td>
                      <td className={`px-4 py-3 ${overdue ? 'text-red-600 font-medium' : 'text-gray-500'}`}>
                        {formatDate(inv.due_date)}
                        {overdue && <span className="ml-1 text-xs">(Overdue)</span>}
                      </td>
                      <td className="px-4 py-3 text-right font-semibold text-gray-900">
                        {formatCurrency(inv.total_amount)}
                      </td>
                      <td className="px-4 py-3 text-right text-green-600">
                        {inv.amount_paid > 0 ? formatCurrency(inv.amount_paid) : <span className="text-gray-300">—</span>}
                      </td>
                      <td className={`px-4 py-3 text-right ${getOutstandingClass(inv)}`}>
                        {outstanding > 0 ? formatCurrency(outstanding) : <span className="text-green-600">Paid</span>}
                      </td>
                      <td className="px-4 py-3 text-center">
                        <StatusBadge status={overdue && inv.status === 'sent' ? 'overdue' : inv.status} />
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-1 justify-end">
                          {inv.status === 'draft' && (
                            <button
                              onClick={() => markSent(inv)}
                              className="text-xs text-blue-600 hover:text-blue-700 font-medium px-2 py-1 rounded hover:bg-blue-50"
                            >
                              Mark Sent
                            </button>
                          )}
                          <Link to={`/invoices/${inv.id}`} className="text-gray-400 hover:text-primary-600 p-1">
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                            </svg>
                          </Link>
                          <button onClick={() => setDeleteTarget(inv)} className="text-gray-400 hover:text-red-500 p-1">
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Mobile cards */}
          <div className="md:hidden space-y-3">
            {filtered.map(inv => {
              const overdue = isOverdue(inv);
              const outstanding = inv.total_amount - (inv.amount_paid || 0);
              return (
                <Link
                  key={inv.id}
                  to={`/invoices/${inv.id}`}
                  className={`block border rounded-lg p-4 hover:bg-gray-50 ${overdue ? 'border-red-100 bg-red-50/30' : 'border-gray-100'}`}
                >
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-primary-600 font-semibold text-sm">{inv.invoice_number}</span>
                    <StatusBadge status={overdue && inv.status === 'sent' ? 'overdue' : inv.status} />
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-gray-700 text-sm">{inv.client_name}</span>
                    <span className="font-bold text-gray-900 text-sm">{formatCurrency(inv.total_amount)}</span>
                  </div>
                  <div className="flex items-center justify-between mt-1">
                    <span className="text-gray-400 text-xs">{formatDate(inv.invoice_date)}</span>
                    {outstanding > 0 ? (
                      <span className={`text-xs ${getOutstandingClass(inv)}`}>
                        Due: {formatCurrency(outstanding)}
                      </span>
                    ) : (
                      <span className="text-xs text-green-600">Paid</span>
                    )}
                  </div>
                </Link>
              );
            })}
          </div>
            </>
          )}
        </>
      )}

      <Modal isOpen={!!deleteTarget} onClose={() => setDeleteTarget(null)} title="Delete Invoice" size="sm">
        <p className="text-gray-600 text-sm">
          Are you sure you want to delete invoice <strong>{deleteTarget?.invoice_number}</strong>? This cannot be undone.
        </p>
        <div className="flex justify-end gap-3 mt-6">
          <button className="btn-secondary" onClick={() => setDeleteTarget(null)}>Cancel</button>
          <button className="btn-danger" onClick={confirmDelete}>Delete</button>
        </div>
      </Modal>
    </div>
  );
}
